// hooks/useStatisticsData.js
import { useState, useCallback } from 'react';
import { statisticsApi } from '../statisticsApi';

export const useApplicationsData = () => {
  const [loading, setLoading] = useState(false);
  const [districtData, setDistrictData] = useState(null);
  const [totalData, setTotalData] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (year, filters) => {
    setLoading(true);
    setError(null);
    try {
      // Получаем данные по районам
      const districtResponse = await statisticsApi.getApplicationsByDistrict(year, filters);
      // Получаем общие данные по Киеву
      const totalResponse = await statisticsApi.getTotalApplications(year, filters);
      
      let transformedDistrict = null;
      let transformedTotal = null;
      
      if (districtResponse.data?.cursor && districtResponse.data.cursor.length > 0) {
        transformedDistrict = transformDistrictData(districtResponse.data.cursor);
      }
      
      if (totalResponse.data?.cursor && totalResponse.data.cursor.length > 0) {
        transformedTotal = transformTotalData(totalResponse.data.cursor);
      }
      
      setDistrictData(transformedDistrict);
      setTotalData(transformedTotal);
    } catch (err) {
      setError(err.message || 'Помилка завантаження даних');
      console.error('Error fetching applications data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  return { districtData, totalData, loading, error, fetchData };
};

export const useApplicationsByKindergartenData = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (year, filters) => {
    setLoading(true);
    setError(null);
    try {
      const response = await statisticsApi.getApplicationsByKindergarten(year, filters);
      
      if (response.data?.cursor && response.data.cursor.length > 0) {
        const transformed = transformKindergartenData(response.data.cursor);
        setData(transformed);
      } else {
        setData(null);
      }
    } catch (err) {
      setError(err.message || 'Помилка завантаження даних');
      console.error('Error fetching kindergarten data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchData };
};

export const useGroupsData = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (filters) => {
    setLoading(true);
    setError(null);
    try {
      const response = await statisticsApi.getGroupsInfo(filters);
      
      if (response.data?.cursor && response.data.cursor.length > 0) {
        const transformed = transformGroupsData(response.data.cursor);
        setData(transformed);
      } else {
        setData(null);
      }
    } catch (err) {
      setError(err.message || 'Помилка завантаження даних');
      console.error('Error fetching groups data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchData };
};

export const useChildrenData = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (params) => {
    setLoading(true);
    setError(null);
    try {
      const response = await statisticsApi.getChildrenStatistics(params);
      
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        const transformed = transformChildrenData(response.data);
        setData(transformed);
      } else {
        setData(null);
      }
    } catch (err) {
      setError(err.message || 'Помилка завантаження даних');
      console.error('Error fetching children data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchData };
};

// Об'єднання старого розділеного Солом'янського району в один — та сама
// нормалізація застосовується і до районної, і до посадочної вибірки, щоб
// назви районів співпадали між таблицями (потрібно для розгортання списку
// садочків усередині рядка району в об'єднаній таблиці заявок).
const isSolomianskyiVariant = (name) => !!name && /Со/.test(name);
const normalizeDistrictName = (name) => (isSolomianskyiVariant(name) ? 'Солом\'янський' : name);

// Функции трансформации данных
function transformDistrictData(cursor) {
  const districts = {};
  
  cursor.forEach(item => {
    const district = item._id.dist;
    const age = parseInt(item._id.age);
    const count = item.count;
    
    if (!districts[district]) {
      districts[district] = {
        1970: 0, 1971: 0, 1972: 0, 1973: 0, 1974: 0, 1975: 0, 1976: 0, 1977: 0
      };
    }
    districts[district][age] = count;
  });
  
  // Объединяем Соломенский район
  let solData = { 1970: 0, 1971: 0, 1972: 0, 1973: 0, 1974: 0, 1975: 0, 1976: 0, 1977: 0 };
  
  Object.keys(districts).forEach(district => {
    if (isSolomianskyiVariant(district)) {
      for (let i = 1970; i <= 1977; i++) {
        solData[i] += districts[district][i];
      }
      delete districts[district];
    }
  });
  
  districts['Солом\'янський'] = solData;
  
  return districts;
}

function transformTotalData(cursor) {
  const totals = { 1970: 0, 1971: 0, 1972: 0, 1973: 0, 1974: 0, 1975: 0, 1976: 0, 1977: 0 };
  
  cursor.forEach(item => {
    totals[item._id] = item.count;
  });
  
  for (let i = 1970; i <= 1977; i++) {
    if (!totals[i]) totals[i] = 0;
  }
  
  return totals;
}

function transformKindergartenData(cursor) {
  const kindergartens = {};
  
  cursor.forEach(element => {
    if (!element.hasOwnProperty('district')) return;
    
    if (!kindergartens[element._id.kgs]) {
      kindergartens[element._id.kgs] = {
        distr: normalizeDistrictName(element.district),
        kgnumber: element.kg_number,
        1970: 0, 1971: 0, 1972: 0, 1973: 0, 1974: 0, 1975: 0, 1976: 0, 1977: 0
      };
    }
    
    kindergartens[element._id.kgs][parseInt(element._id.age)] = element.count;
  });
  
  return kindergartens;
}

function transformGroupsData(cursor) {
  return cursor.map(item => ({
    kgId: item._id.kg_id,
    number: item._id.number,
    district: normalizeDistrictName(item._id.distr),
    kyiv: item.kyiv || 0,
    groups: item.groups.map(group => ({
      groupId: group._id,
      number: group.number,
      spec: group.spec,
      disabilitiesType: group.disabilitiesType,
      type: group.type,
      typeName: group.typeName,
      numberSite: group.number_site,
      planedSite: group.planed_site || {},
      info: group.info || [],
      reserv: group.reserv || []
    }))
  }));
}

function transformChildrenData(data) {
  const districts = {};
  const totals = { 1970: 0, 1971: 0, 1972: 0, 1973: 0, 1974: 0, 1975: 0, 1976: 0, 1977: 0, 1978: 0 };
  const kindergartensMap = {};

  data.forEach(item => {
    const district = item._id.district;
    const age = parseInt(item._id.age);
    const sum = item.sum;
    const kgId = item._id.kg_id;
    const kgNumber = item._id.kgNumber;

    if (!districts[district]) {
      districts[district] = {
        1970: 0, 1971: 0, 1972: 0, 1973: 0, 1974: 0, 1975: 0, 1976: 0, 1977: 0, 1978: 0
      };
    }
    // Тепер один й той самий район+вік може прийти кількома рядками (по
    // одному на кожен садочок, бо бекенд групує ще й по kg_id) — тому
    // підсумовуємо, а не перезаписуємо, як було раніше при групуванні
    // виключно по district+age.
    districts[district][age] += sum;
    totals[age] = (totals[age] || 0) + sum;

    if (kgId) {
      const key = String(kgId);
      if (!kindergartensMap[key]) {
        kindergartensMap[key] = {
          kgId: key,
          kgNumber,
          district,
          1970: 0, 1971: 0, 1972: 0, 1973: 0, 1974: 0, 1975: 0, 1976: 0, 1977: 0, 1978: 0
        };
      }
      kindergartensMap[key][age] += sum;
    }
  });

  // Объединяем Соломенский район
  let solData = { 1970: 0, 1971: 0, 1972: 0, 1973: 0, 1974: 0, 1975: 0, 1976: 0, 1977: 0, 1978: 0 };

  Object.keys(districts).forEach(district => {
    if (district.match(/Со/)) {
      for (let i = 1970; i <= 1978; i++) {
        solData[i] += districts[district][i];
      }
      delete districts[district];
    }
  });

  districts['Солом\'янський'] = solData;

  // Той самий Солом'янський мердж — і для назви району в кожному садочку,
  // щоб група по kg_id.district співпадала з ключами districts вище
  // (потрібно для розгортання садочків усередині рядка району).
  Object.values(kindergartensMap).forEach(kg => {
    kg.district = normalizeDistrictName(kg.district);
  });

  return { districts, totals, kindergartens: kindergartensMap };
}

// Константы для типов мест
export const types_places = [
  { name: 'Повного дня', value: 'sleepingPlaces' },
  { name: 'Короткотривалого перебування', value: 'withoutBeds' },
  { name: 'Інклюзія', value: 'inclusive' }
];

// Константы для возрастных категорий групп
export const groupAgeCategories = [
  { number: 2, ageLimitFrom: 1, ageLimitTo: 2, description: "Друга група раннього віку — від 1-го до 2-х років" },
  { number: 3, ageLimitFrom: 2, ageLimitTo: 3, description: "Перша молодша група — від 2-х до 3-х років" },
  { number: 4, ageLimitFrom: 3, ageLimitTo: 4, description: "Друга молодша група — від 3-х до 4-х років" },
  { number: 5, ageLimitFrom: 4, ageLimitTo: 5, description: "Середня група — від 4-х до 5-ти років" },
  { number: 6, ageLimitFrom: 5, ageLimitTo: 6, description: "Старша група — від 5-ти до 6-ти років" },
  { number: 10, ageLimitFrom: 3, ageLimitTo: 5, description: "Різновікова — від 3-х до 5-ти років" },
  { number: 11, ageLimitFrom: 3, ageLimitTo: 6, description: "Різновікова — від 3-х до 6-ти років" },
  { number: 12, ageLimitFrom: 4, ageLimitTo: 6, description: "Різновікова — від 4-х до 6-ти років" },
  { number: 14, ageLimitFrom: 1, ageLimitFromPlusMonth: 6, ageLimitTo: 3, description: "Від 1 року і 6 місяців до 3 років" },
  { number: 16, ageLimitFrom: 1, ageLimitTo: 3, description: "Від 1-го до 3 років" },
  { number: 17, ageLimitFrom: 1, ageLimitFromPlusMonth: 6, ageLimitTo: 3, description: "Від 6 місяців до року" },
  { number: 18, ageLimitFrom: 2, ageLimitTo: 6, description: "Різновікова — від 2-х до 6-ти років"}
];